// Reading and writing one recipient's data by the dotted paths rules are written against.
//
// One definition of a path for both directions, so a value the preview writes is exactly the
// value a rule then reads — `data.a[0].b` means the same step list to each.
import type { SmartFieldsAvailableSources } from '../smartFields';

/** Bracket indices are rewritten to dotted steps so one split handles both forms. */
const pathSteps = (path: string): string[] => path.replace(/\[(\d+)\]/g, '.$1').split('.');

/** Steps that would write onto a prototype rather than into the data. */
const UNSAFE_STEPS = new Set(['__proto__', 'prototype', 'constructor']);

/** `a.b[0].c` → the value, or undefined the moment any step is missing. */
export const readFlowDataPath = (root: unknown, path: string): unknown => {
	let current: unknown = root;
	for (const step of pathSteps(path)) {
		if (current === null || current === undefined || step === '') {
			return undefined;
		}
		if (Array.isArray(current)) {
			const index = Number(step);
			current = Number.isInteger(index) ? current[index] : undefined;
			continue;
		}
		if (typeof current !== 'object') {
			return undefined;
		}
		current = (current as Record<string, unknown>)[step];
	}
	return current;
};

/**
 * The path holding "has this declaration been answered?", for a declaration path.
 *
 * `data.declarations.fundsSource.salary` → `data.declarations.answered.fundsSource.salary`.
 * Mechanical rather than a lookup table, so a declaration added to the projection is covered
 * without anyone remembering to register it.
 */
const ANSWERED_PREFIX = 'data.declarations.';
export const answeredPathFor = (path: string): string | null =>
	path.startsWith(ANSWERED_PREFIX) && !path.startsWith(`${ANSWERED_PREFIX}answered.`)
		? `${ANSWERED_PREFIX}answered.${path.slice(ANSWERED_PREFIX.length)}`
		: null;

/**
 * `data.a.list[0].field` → the list's path and the field, for a data point read off one entry of
 * a list. A rule on such a point reads EVERY entry: "tax residence is the US" is about any of
 * the investor's tax residences, not whichever one happens to sort first.
 */
export const listPointParts = (path: string): { list: string; field: string } | null => {
	const match = /^(.*)\[0\]\.([^.[\]]+)$/.exec(path);
	const list = match?.[1];
	const field = match?.[2];
	return list === undefined || field === undefined ? null : { list, field };
};

export const isBlankFlowValue = (value: unknown): boolean => {
	if (value === null || value === undefined) {
		return true;
	}
	if (Array.isArray(value)) {
		return value.length === 0;
	}
	// Deliberately NOT treating "N/A", "-" or "TBC" as blank. They are values somebody typed,
	// and silently reading them as absent would drop a clause the author never agreed to drop.
	return String(value).trim() === '';
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

/** `container` with `steps` set to `leaf`, copying only the containers on the way down. */
const withValueAt = (container: unknown, steps: string[], leaf: unknown): unknown => {
	const [step, ...rest] = steps;
	if (step === undefined) {
		return leaf;
	}
	if (Array.isArray(container)) {
		const index = Number(step);
		// No further than one past the end: a huge index would make a sparse array that every
		// later copy has to walk.
		if (!Number.isInteger(index) || index < 0 || index > container.length) {
			return container;
		}
		const copy = [...container];
		copy[index] = withValueAt(copy[index], rest, leaf);
		return copy;
	}
	const base = isRecord(container) ? container : {};
	return { ...base, [step]: withValueAt(base[step], rest, leaf) };
};

/**
 * A copy of `data` with the value at a `data.…` path replaced.
 *
 * Copy-on-write along the path only. A deep clone would turn every class instance in a real
 * recipient's data — Prisma's `Decimal`, say — into a plain object that prints as
 * "[object Object]" on the page. A path outside `data`, or one stepping onto a prototype, leaves
 * the data as it was.
 */
export const withFlowDataValue = (
	data: SmartFieldsAvailableSources,
	path: string,
	value: unknown
): SmartFieldsAvailableSources => {
	const [root, top, ...rest] = pathSteps(path);
	const steps = [top, ...rest];
	if (
		root !== 'data' ||
		top === undefined ||
		steps.some((step) => step === undefined || step === '' || UNSAFE_STEPS.has(step))
	) {
		return data;
	}
	return { ...data, [top]: withValueAt(readFlowDataPath(data, top), rest, value) };
};
